import { Spinner } from './feedback';
import { OperationBlockedContext } from './OperationBoundary';
import { useContext } from 'react';
import { EditBoundary, EditOutletContext } from './EditBoundary';
import type { LockResource } from '@mcpanel/core';
import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { ServerState } from '../api/types';
import { stateInfo } from '../format';

type Tone = 'ok' | 'warn' | 'danger' | 'info' | 'muted';

export function Badge({ tone, children }: { tone: Tone; children: ReactNode }) {
  return (
    <span className={`badge ${tone}`}>
      <span className="badge-dot" aria-hidden />
      {children}
    </span>
  );
}

export function StateBadge({ state, unreachable = false }: { state: ServerState | 'unknown'; unreachable?: boolean }) {
  // 稼働中のはずなのに agent から監視値が届かない（ノードや VM が落ちたときなど）
  const info = unreachable
    ? { tone: 'danger' as const, label: '応答なし', busy: false }
    : state === 'unknown'
      ? { tone: 'muted' as const, label: '不明', busy: false }
      : stateInfo[state];
  return (
    <Badge tone={info.tone}>
      {info.busy && <Spinner />}
      {info.label}
    </Badge>
  );
}

export function Meter({
  label,
  value,
  max,
  detail,
  neutral = false,
}: {
  label: string;
  value: number;
  max: number;
  detail: string;
  /** Java のメモリなど、使用率が高くても異常ではない値は警告色にしない */
  neutral?: boolean;
}) {
  const ratio = max > 0 ? Math.min(1, value / max) : 0;
  const tone = neutral ? '' : ratio >= 0.9 ? ' danger' : ratio >= 0.75 ? ' warn' : '';
  return (
    <div className="meter">
      <div className="meter-head">
        <span>{label}</span>
        <span>{detail}</span>
      </div>
      <div
        className="meter-track"
        role="meter"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={max}
        aria-valuenow={value}
      >
        <div className={`meter-fill${tone}`} style={{ width: `${ratio * 100}%` }} />
      </div>
    </div>
  );
}

export function Stat({ label, value, unit }: { label: string; value: ReactNode; unit?: string }) {
  return (
    <div>
      <div className="stat-label">{label}</div>
      <div className="stat-value">
        {value}
        {unit && <small> {unit}</small>}
      </div>
    </div>
  );
}

export function Dialog({
  title,
  children,
  onClose,
  actions,
  wide = false,
  className,
  lockResource,
  revision,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
  actions: ReactNode;
  wide?: boolean;
  /** 画面ごとの幅などを足す */
  className?: string;
  lockResource?: LockResource;
  revision?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCloseRef.current();
    };
    document.addEventListener('keydown', onKey);
    ref.current?.querySelector<HTMLElement>('input, select, button')?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      previous?.focus();
    };
  }, []);
  return createPortal(
    <div className="dialog-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div
        className={`dialog${wide ? ' wide' : ''}${className ? ` ${className}` : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        ref={ref}
      >
        <div className="row">
          <h2>{title}</h2>
          <span className="spacer" />
          <button className="btn ghost small" aria-label="ダイアログを閉じる" onClick={onClose}>
            ×
          </button>
        </div>
        {/* ダイアログは body に出るので画面の編集ロックの外。承認の欄はダイアログの中にそのまま出す */}
        <EditOutletContext.Provider value={null}>
          <DialogOperationBoundary>
            {lockResource ? (
              <EditBoundary resource={lockResource} revision={revision} automatic>
                {children}
                <div className="dialog-actions">{actions}</div>
              </EditBoundary>
            ) : (
              <>
                {children}
                <div className="dialog-actions">{actions}</div>
              </>
            )}
          </DialogOperationBoundary>
        </EditOutletContext.Provider>
      </div>
    </div>,
    document.body,
  );
}

function DialogOperationBoundary({ children }: { children: ReactNode }) {
  const blocked = useContext(OperationBlockedContext);
  if (!blocked) return <>{children}</>;
  return (
    <fieldset disabled style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
      {children}
    </fieldset>
  );
}

export function ErrorAlert({ error }: { error: Error | undefined }) {
  if (!error) return null;
  return <div className="alert danger">{error.message}</div>;
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>;
}
